/**
 * /dashboard/payroll/statutory — G_STAT statutory register & return
 * export hub.
 *
 * Surfaces every statutory CSV that can be generated from locked payroll
 * snapshots (Companies Act 2013 §128(1) anchor — only locked runs are
 * exportable). The page is HR-admin only (same RBAC as every
 * /api/v1/payroll/* route server-side).
 *
 * Tile inventory (8 wired, 2 coming-soon):
 *   1. EPFO ECR          — per locked run  (EPF Act §6)
 *   2. ESI half-yearly   — H1/H2 window    (ESI Act §39)
 *   3. PT challan        — per locked run  (state PT Acts)
 *   4. Bonus Form D      — FY              (Payment of Bonus Act §26)
 *   5. S&E annual return — calendar year   (state S&E Acts)
 *   6. ER Form D         — quarter         (ER Act 1976 §8)
 *   7. LWF biannual      — H1/H2 window    (state LWF Acts)
 *   8. Maternity §11A    — FY              (Maternity Benefit Act §11A)
 *   9. Form 11 (EPF)     — per employee    [Coming soon — per-employee
 *                                           JSON endpoint; bulk CSV queued]
 *  10. Form F (Gratuity) — per employee    [Coming soon — same reason]
 *
 * Pattern mirrors app.$tenantSlug.accounting.returns.tsx exactly.
 * No generate/POST flow here — all are direct GET CSV downloads once the
 * period params are set.
 */

import { useState } from "react";
import {
  createFileRoute,
  Link,
  useParams,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  Download,
  FileText,
  Clock,
} from "lucide-react";
import { PayrollModuleShell } from "@/components/shared/payroll-module-shell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/payroll/statutory",
)({
  component: StatutoryRoute,
});

// ---------------------------------------------------------------------------
// Period helpers
// ---------------------------------------------------------------------------

function currentYear(): number {
  return new Date().getUTCFullYear();
}

function currentMonth(): number {
  return new Date().getUTCMonth() + 1;
}

/** Current Indian FY year (Apr start). */
function currentFYYear(): number {
  const m = currentMonth();
  return m >= 4 ? currentYear() : currentYear() - 1;
}

/** Previous calendar-half (for LWF biannual — file AFTER half ends). */
function prevCalendarHalf(): { half: 1 | 2; year: number } {
  if (currentMonth() <= 6) {
    // We're in H1 → previous was H2 of last year
    return { half: 2, year: currentYear() - 1 };
  }
  return { half: 1, year: currentYear() };
}

/** ESI statutory half — H1=Apr-Sep, H2=Oct-Mar of FY. */
function currentESIHalf(): 1 | 2 {
  const m = currentMonth();
  return m >= 4 && m <= 9 ? 1 : 2;
}

function yearRange(): number[] {
  const fy = currentFYYear();
  const years: number[] = [];
  for (let y = fy; y >= 2021; y--) years.push(y);
  return years;
}

function calendarYearRange(): number[] {
  const y = currentYear();
  const years: number[] = [];
  for (let i = y; i >= 2021; i--) years.push(i);
  return years;
}

const QUARTER_LABELS: Record<number, string> = {
  1: "Q1 (Jan–Mar)", 2: "Q2 (Apr–Jun)", 3: "Q3 (Jul–Sep)", 4: "Q4 (Oct–Dec)",
};

// ESI windows per ESI Act §39: H1 = Apr 01 – Sep 30; H2 = Oct 01 – Mar 31.
function esiWindow(fyYear: number, half: 1 | 2): { from: string; to: string } {
  if (half === 1) {
    return { from: `${fyYear}-04-01`, to: `${fyYear}-09-30` };
  }
  // H2: Oct of fyYear to Mar of fyYear+1
  return { from: `${fyYear}-10-01`, to: `${fyYear + 1}-03-31` };
}

// ---------------------------------------------------------------------------
// API URL builders — mirrors backend route shape exactly.
// ---------------------------------------------------------------------------

const BASE = "/api/v1/payroll";

function ecrURL(runId: number): string {
  return `${BASE}/runs/${runId}/ecr.csv?download=1`;
}

function esiURL(fyYear: number, half: 1 | 2): string {
  const { from, to } = esiWindow(fyYear, half);
  return `${BASE}/esi-return.csv?from=${from}&to=${to}&download=1`;
}

function ptChallanURL(runId: number): string {
  return `${BASE}/runs/${runId}/pt-challan.csv?download=1`;
}

function bonusFormDURL(fyYear: number): string {
  return `${BASE}/bonus-form-d.csv?fy_start=${fyYear}-04-01&download=1`;
}

function seReturnURL(year: number): string {
  // Wave F backend handler: GET /api/v1/payroll/g-stat/s-e?fy=YYYY-YY
  // The page passes a calendar year; convert to FY label (KA S&E uses
  // calendar year, but the handler is FY-shaped; year as fy-start works).
  const fy = `${year}-${String((year + 1) % 100).padStart(2, "0")}`;
  return `${BASE}/g-stat/s-e?fy=${fy}`;
}

// FY label helper — Indian FY runs Apr→Mar, e.g. fyYear=2025 → "2025-26".
function fyLabel(fyYear: number): string {
  return `${fyYear}-${String((fyYear + 1) % 100).padStart(2, "0")}`;
}

// G_STAT (Wave F) — backend handlers now wired.
// All four return text/csv; the browser handles the download via Content-Disposition.

function erRegisterURL(fyYear: number, _quarter: number): string {
  // Equal Remuneration Form D is an ANNUAL return (ER Act 1976 §8),
  // not quarterly. The quarter param remains in the signature for
  // back-compat with the existing form but is ignored.
  return `${BASE}/g-stat/er?fy=${fyLabel(fyYear)}`;
}

function lwfURL(year: number, half: 1 | 2): string {
  return `${BASE}/g-stat/lwf?year=${year}&period=H${half}`;
}

function maternityReturnURL(fyYear: number): string {
  return `/api/v1/maternity-cases/annual-return?fy=${fyLabel(fyYear)}`;
}

// ---------------------------------------------------------------------------
// Shared tile shape
// ---------------------------------------------------------------------------

interface WiredTileProps {
  title: string;
  description: string;
  cadence: string;
  statutoryNote: string;
  href: string;
}

interface ComingSoonTileProps {
  title: string;
  description: string;
  cadence: string;
  reason: string;
}

// ---------------------------------------------------------------------------
// WiredTile — direct GET CSV download link
// ---------------------------------------------------------------------------

function WiredTile({
  title,
  description,
  cadence,
  statutoryNote,
  href,
}: Readonly<WiredTileProps>) {
  return (
    <Card data-testid={`statutory-tile-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
              <FileText className="h-4 w-4" />
            </span>
            <div>
              <CardTitle className="text-sm font-semibold">{title}</CardTitle>
              <CardDescription className="text-xs">{description}</CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="shrink-0 text-xs capitalize">
            {cadence}
          </Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{statutoryNote}</p>
      </CardHeader>
      <CardContent>
        <Button
          asChild
          variant="outline"
          size="sm"
          className="w-full gap-1.5"
        >
          <a href={href} download target="_blank" rel="noreferrer">
            <Download className="h-3.5 w-3.5" />
            Download CSV
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ComingSoonTile
// ---------------------------------------------------------------------------

function ComingSoonTile({
  title,
  description,
  cadence,
  reason,
}: Readonly<ComingSoonTileProps>) {
  return (
    <Card
      className="opacity-70"
      data-testid={`statutory-tile-coming-soon-${title.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <Clock className="h-4 w-4" />
            </span>
            <div>
              <CardTitle className="text-sm font-semibold">{title}</CardTitle>
              <CardDescription className="text-xs">{description}</CardDescription>
            </div>
          </div>
          <Badge variant="secondary" className="shrink-0 text-xs">
            {cadence}
          </Badge>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{reason}</p>
      </CardHeader>
      <CardContent>
        <Button size="sm" variant="outline" className="w-full" disabled>
          Coming soon
        </Button>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main route
// ---------------------------------------------------------------------------

function StatutoryRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };

  // Shared period state — each tile reads its own relevant slice.
  const [fyYear, setFYYear] = useState<number>(currentFYYear);
  const [esiHalf, setESIHalf] = useState<1 | 2>(currentESIHalf);
  const [esiFYYear, setESIFYYear] = useState<number>(currentFYYear);
  const [calYear, setCalYear] = useState<number>(currentYear);
  const [erYear, setERYear] = useState<number>(currentYear);
  const [erQuarter, setERQuarter] = useState<number>(1);
  const [lwfYear, setLWFYear] = useState<number>(prevCalendarHalf().year);
  const [lwfHalf, setLWFHalf] = useState<1 | 2>(prevCalendarHalf().half);
  const [maternityFYYear, setMaternityFYYear] = useState<number>(currentFYYear);

  // Per-run tiles: ECR + PT need a locked run ID.
  const [ecrRunId, setECRRunId] = useState<string>("");
  const [ptRunId, setPTRunId] = useState<string>("");

  // Derived download URLs.
  const ecrHref = ecrRunId.trim() ? ecrURL(Number(ecrRunId.trim())) : "#";
  const ptHref = ptRunId.trim() ? ptChallanURL(Number(ptRunId.trim())) : "#";

  return (
    <PayrollModuleShell
      title="Statutory Exports"
      description="Download statutory registers and returns as CSV files for portal upload. All exports require locked payroll runs (Companies Act 2013 §128(1))."
      actions={
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/payroll" params={{ tenantSlug }}>
            <ArrowLeft className="h-4 w-4" />
            Payroll
          </Link>
        </Button>
      }
    >
      {/* ----------------------------------------------------------------- */}
      {/* Period selector strip                                              */}
      {/* ----------------------------------------------------------------- */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-end gap-4">
            {/* FY year — Bonus Form D + Maternity §11A */}
            <div className="grid gap-1.5">
              <Label htmlFor="stat-fy-year">FY Year (Bonus / Maternity)</Label>
              <Select
                value={String(fyYear)}
                onValueChange={(v) => {
                  setFYYear(Number(v));
                  setMaternityFYYear(Number(v));
                }}
              >
                <SelectTrigger id="stat-fy-year" className="w-40" data-testid="stat-fy-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearRange().map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      FY {y}–{String(y + 1).slice(2)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Calendar year — S&E annual */}
            <div className="grid gap-1.5">
              <Label htmlFor="stat-cal-year">Calendar Year (S&amp;E)</Label>
              <Select
                value={String(calYear)}
                onValueChange={(v) => setCalYear(Number(v))}
              >
                <SelectTrigger id="stat-cal-year" className="w-36" data-testid="stat-cal-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {calendarYearRange().map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ER quarter */}
            <div className="grid gap-1.5">
              <Label htmlFor="stat-er-year">ER Quarter</Label>
              <div className="flex gap-2">
                <Select
                  value={String(erYear)}
                  onValueChange={(v) => setERYear(Number(v))}
                >
                  <SelectTrigger id="stat-er-year" className="w-28" data-testid="stat-er-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {calendarYearRange().map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={String(erQuarter)}
                  onValueChange={(v) => setERQuarter(Number(v))}
                >
                  <SelectTrigger id="stat-er-quarter" className="w-36" data-testid="stat-er-quarter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4].map((q) => (
                      <SelectItem key={q} value={String(q)}>
                        {QUARTER_LABELS[q]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* ESI half-year — FY-scoped */}
            <div className="grid gap-1.5">
              <Label htmlFor="stat-esi-fy">ESI Half-Year</Label>
              <div className="flex gap-2">
                <Select
                  value={String(esiFYYear)}
                  onValueChange={(v) => setESIFYYear(Number(v))}
                >
                  <SelectTrigger id="stat-esi-fy" className="w-28" data-testid="stat-esi-fy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearRange().map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        FY {y}–{String(y + 1).slice(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={String(esiHalf)}
                  onValueChange={(v) => setESIHalf(Number(v) as 1 | 2)}
                >
                  <SelectTrigger id="stat-esi-half" className="w-36" data-testid="stat-esi-half">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">H1 (Apr–Sep)</SelectItem>
                    <SelectItem value="2">H2 (Oct–Mar)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* LWF biannual — calendar-year-scoped */}
            <div className="grid gap-1.5">
              <Label htmlFor="stat-lwf-year">LWF Half-Year</Label>
              <div className="flex gap-2">
                <Select
                  value={String(lwfYear)}
                  onValueChange={(v) => setLWFYear(Number(v))}
                >
                  <SelectTrigger id="stat-lwf-year" className="w-28" data-testid="stat-lwf-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {calendarYearRange().map((y) => (
                      <SelectItem key={y} value={String(y)}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={String(lwfHalf)}
                  onValueChange={(v) => setLWFHalf(Number(v) as 1 | 2)}
                >
                  <SelectTrigger id="stat-lwf-half" className="w-36" data-testid="stat-lwf-half">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">H1 (Jan–Jun)</SelectItem>
                    <SelectItem value="2">H2 (Jul–Dec)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Per-run IDs — ECR + PT require a locked run ID */}
          <div className="mt-4 flex flex-wrap gap-4 border-t pt-4">
            <div className="grid gap-1.5">
              <Label htmlFor="stat-ecr-run-id">
                ECR / PT Challan — Locked Run ID
              </Label>
              <p className="text-xs text-muted-foreground">
                Enter the payroll run ID from the Payroll list. The run must
                be locked before the ECR or PT challan can be exported.
              </p>
            </div>
            <div className="flex gap-2">
              <div className="grid gap-1.5">
                <Label htmlFor="stat-ecr-run-id-input" className="sr-only">Run ID for ECR</Label>
                <Input
                  id="stat-ecr-run-id-input"
                  className="w-32"
                  placeholder="Run ID"
                  value={ecrRunId}
                  onChange={(e) => {
                    setECRRunId(e.target.value);
                    setPTRunId(e.target.value);
                  }}
                  data-testid="stat-run-id-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ----------------------------------------------------------------- */}
      {/* Tile grid                                                          */}
      {/* ----------------------------------------------------------------- */}
      <div className="mt-4 grid gap-4 sm:grid-cols-2">

        {/* 1. EPFO ECR */}
        {ecrRunId.trim() ? (
          <WiredTile
            title="EPFO ECR — Monthly Contribution"
            description={`Payroll run #${ecrRunId.trim()}`}
            cadence="Monthly"
            statutoryNote="EPF Act 1952 §6 — Electronic Challan cum Return; upload to EPFO unified-portal-emp."
            href={ecrHref}
          />
        ) : (
          <Card data-testid="statutory-tile-epfo-ecr-pending">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div>
                    <CardTitle className="text-sm font-semibold">EPFO ECR — Monthly Contribution</CardTitle>
                    <CardDescription className="text-xs">Enter a locked Run ID above to enable</CardDescription>
                  </div>
                </div>
                <Badge variant="outline" className="shrink-0 text-xs">Monthly</Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                EPF Act 1952 §6 — Electronic Challan cum Return; upload to EPFO unified-portal-emp.
              </p>
            </CardHeader>
            <CardContent>
              <Button size="sm" variant="outline" className="w-full" disabled>
                Enter Run ID to download
              </Button>
            </CardContent>
          </Card>
        )}

        {/* 2. ESI Half-Yearly Return */}
        <WiredTile
          title="ESI Half-Yearly Return"
          description={`FY ${esiFYYear}–${String(esiFYYear + 1).slice(2)}, ${esiHalf === 1 ? "H1 (Apr–Sep)" : "H2 (Oct–Mar)"}`}
          cadence="Half-Yearly"
          statutoryNote="ESI Act 1948 §39 — Aggregates every locked run in the half; refuses if zero locked runs found."
          href={esiURL(esiFYYear, esiHalf)}
        />

        {/* 3. PT Challan */}
        {ptRunId.trim() ? (
          <WiredTile
            title="Professional Tax Challan"
            description={`Payroll run #${ptRunId.trim()}`}
            cadence="Monthly"
            statutoryNote="State PT Acts (TN/KA/MH/WB/GJ; Constitution Art. 276 ₹2,500/yr cap) — per-state rows."
            href={ptHref}
          />
        ) : (
          <Card data-testid="statutory-tile-pt-challan-pending">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground">
                    <FileText className="h-4 w-4" />
                  </span>
                  <div>
                    <CardTitle className="text-sm font-semibold">Professional Tax Challan</CardTitle>
                    <CardDescription className="text-xs">Enter a locked Run ID above to enable</CardDescription>
                  </div>
                </div>
                <Badge variant="outline" className="shrink-0 text-xs">Monthly</Badge>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                State PT Acts (TN/KA/MH/WB/GJ; Constitution Art. 276 ₹2,500/yr cap) — per-state rows.
              </p>
            </CardHeader>
            <CardContent>
              <Button size="sm" variant="outline" className="w-full" disabled>
                Enter Run ID to download
              </Button>
            </CardContent>
          </Card>
        )}

        {/* 4. Bonus Form D */}
        <WiredTile
          title="Bonus Form D — Annual Return"
          description={`FY ${fyYear}–${String(fyYear + 1).slice(2)} (Apr ${fyYear} – Mar ${fyYear + 1})`}
          cadence="Annual (FY)"
          statutoryNote="Payment of Bonus Act 1965 §26 + Rules 1975 Rule 5 — all 12 months must be locked."
          href={bonusFormDURL(fyYear)}
        />

        {/* 5. S&E Annual Register */}
        <WiredTile
          title="Shops &amp; Establishments Annual Register"
          description={`Calendar year ${calYear}`}
          cadence="Annual"
          statutoryNote="State Shops &amp; Establishments Acts — all 12 calendar months must have locked runs."
          href={seReturnURL(calYear)}
        />

        {/* 6. Equal Remuneration Form D */}
        <WiredTile
          title="Equal Remuneration Register (Form D)"
          description={`${erYear} ${QUARTER_LABELS[erQuarter]}`}
          cadence="Quarterly"
          statutoryNote="Equal Remuneration Act 1976 §8 + Rules 1976 Rule 6 — all 3 months of the quarter must be locked."
          href={erRegisterURL(erYear, erQuarter)}
        />

        {/* 7. LWF Biannual */}
        <WiredTile
          title="LWF Biannual Contribution"
          description={`${lwfYear}, ${lwfHalf === 1 ? "H1 (Jan–Jun)" : "H2 (Jul–Dec)"}`}
          cadence="Biannual"
          statutoryNote="State Labour Welfare Fund Acts — Jan-Jun OR Jul-Dec of the same year; 5 states seeded (TN/KA/MH/WB/GJ)."
          href={lwfURL(lwfYear, lwfHalf)}
        />

        {/* 8. Maternity §11A Annual Return */}
        <WiredTile
          title="Maternity Benefit §11A Annual Return"
          description={`FY ${maternityFYYear}–${String(maternityFYYear + 1).slice(2)}`}
          cadence="Annual (FY)"
          statutoryNote="Maternity Benefit Act 1961 §11A — per approved ML instance overlapping the FY; female employees only (§5(2))."
          href={maternityReturnURL(maternityFYYear)}
        />

        {/* 9. Form 11 — Coming Soon */}
        <ComingSoonTile
          title="Form 11 (EPF Non-Enrolment Declaration)"
          description="Per-employee — EPF Scheme Para 34"
          cadence="Per employee"
          reason="Coming soon — backend G_STAT.2a queued. Individual employee endpoint wired at GET /api/v1/employees/{id}/form-11; bulk HR export CSV queued."
        />

        {/* 10. Form F — Coming Soon */}
        <ComingSoonTile
          title="Form F (Gratuity Nomination)"
          description="Per-employee — Payment of Gratuity Act 1972 §6"
          cadence="Per employee"
          reason="Coming soon — backend G_STAT.2a queued. Individual employee endpoint wired at GET /api/v1/employees/{id}/form-f; bulk HR export CSV queued."
        />
      </div>
    </PayrollModuleShell>
  );
}
